/**
 * 已启用充值链列表（GET /deposit/blockchains）的全局缓存。
 * 与登录页、充值弹窗等共享；`data[0]` 视为当前默认链。
 */

import { create } from "zustand";
import { getActiveBlockchains } from "@/api";
import type { BlockchainConfig } from "@/api";

export type DepositBlockchainsStatus = "idle" | "loading" | "success" | "error";

export type RefreshBlockchainsOptions = {
  /** 为 true 时始终请求（如登录页「重试」）；默认 false 且在已成功拉取过时跳过，避免重复打 `/deposit/blockchains` */
  force?: boolean;
};

export interface DepositBlockchainsState {
  blockchains: BlockchainConfig[];
  status: DepositBlockchainsStatus;
  errorMessage: string | null;
  /**
   * 拉取链列表。
   * - 默认：`status === "success"` 时直接返回（内存缓存命中）。
   * - `force: true`：忽略缓存再请求。
   * - `loading` 中再次调用会短路（与原先并发行为一致）。
   */
  refreshBlockchains: (options?: RefreshBlockchainsOptions) => Promise<void>;
}

export const useDepositBlockchainsStore = create<DepositBlockchainsState>((set, get) => ({
  blockchains: [],
  status: "idle",
  errorMessage: null,

  refreshBlockchains: async (options?: RefreshBlockchainsOptions) => {
    const force = options?.force === true;
    if (!force && get().status === "success") return;
    if (get().status === "loading") return;
    set({ status: "loading", errorMessage: null });
    const res = await getActiveBlockchains();
    if (res.ok) {
      set({
        blockchains: res.data ?? [],
        status: "success",
        errorMessage: null,
      });
    } else {
      set({
        blockchains: [],
        status: "error",
        errorMessage: res.error.message || "Failed to load chains",
      });
    }
  },
}));

/** 当前默认链（接口列表第 0 条），无数据时为 null */
export function selectPrimaryBlockchain(s: DepositBlockchainsState): BlockchainConfig | null {
  return s.blockchains[0] ?? null;
}
